import {animate, state, style, transition, trigger} from '@angular/animations';
import {APP_BASE_HREF} from '@angular/common';
import {Component, Inject, Injector, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {combineLatest, Observable, Subscription, zip} from 'rxjs';
import {filter, map, startWith} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {AccountType} from '../../../model';
import {cache, closest} from '../../../utils';
import {AppStateService} from '../../shared/app-state.service';
import {AuthService} from '../../shared/auth.service';
import {IdentifyModalComponent} from '../../shared/identify-modal/identify-modal.component';
import {StorageService} from '../../shared/storage.service';
import {TourService} from '../../shared/tour.service';

@Component({
  selector: 'app-header',
  templateUrl: './app-header.component.html',
  styleUrls: ['./app-header.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideInOut', [
      state('in', style({transform: 'translateX(0)'})),
      transition('void => in', [
        style({transform: 'translateX(-100%)'}),
        animate(300)
      ]),
      transition('in => void', [
        animate(300, style({transform: 'translateX(-100%)'}))
      ])
    ])
  ]
})
export class AppHeaderComponent implements OnInit, OnDestroy {

  minimal$ = this.AppState.minimal$;
  navOpen$ = this.AppState.navOpen$;
  user$ = this.Auth.user$;
  brand$ = this.AppState.brand$;
  locale$ = this.AppState.locale$;
  isAustralia$ = this.AppState.isAustralia$;
  effectiveUser$ = combineLatest(this.Auth.user$, this.Auth.temporaryUser$, (user, temp) => user || temp);
  state$: Observable<string | null>;
  isAdmin$ = this.user$.pipe(map(user => user && user.admin));
  username$ = this.user$.pipe(map(user => user && user.username));
  refreshingUser$ = this.Auth.refreshingUser$;
  viewHeader$: Observable<boolean>;

  masqueradingBanner$ = combineLatest(
    this.Auth.masquerading$,
    this.AppState.minimal$,
    (masquerading, minimal) => masquerading && !minimal
  );

  timecardsOnly$ = this.effectiveUser$
    .pipe(map(user => user && user.accountTypeId === AccountType.TimecardOnly));
  invoicingAllowed$ = this.effectiveUser$
    .pipe(map(user => user && user.invoicingAllowed));

  reportsTab = environment.flags.reportsTab;

  showDiversityInsights$ = this.effectiveUser$
      .pipe(map(user => user && user.showDiversityInsights));

  @ViewChild('identifyModal') identifyModal: IdentifyModalComponent;
  identificationCallback: Function | null = null;

  learnMoreCurrStep = 1;
  learnMoreMaxStep = 3;

  private tourSub?: Subscription;

  constructor(private AppState: AppStateService,
              private Auth: AuthService,
              private Router: Router,
              private route: ActivatedRoute,
              private Tour: TourService,
              private Storage: StorageService,
              private injector: Injector,
              @Inject(APP_BASE_HREF) private basePath: string) {
  }

  ngOnInit() {
    this.state$ = this.Router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        startWith(null),
        map(() => {
          const snapshot = this.Router.routerState.snapshot.root.firstChild;
          const routeData = snapshot && snapshot.data;
          let category = routeData && routeData.category;
          if (typeof category === 'function') {
            category = category(snapshot);
          }
          return category || null;
        }),
        cache()
      );

    this.tourSub = zip(this.effectiveUser$, this.timecardsOnly$)
      .subscribe(([user, timecardsOnly]) => user && !timecardsOnly
        ? this.Tour.startTour('requestTalentHeader')
        : this.Tour.stopTour('requestTalentHeader', false));

    this.viewHeader$ = this.Router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        map( () => {
          const snapshot = this.Router.routerState.snapshot.root.firstChild;
          return (snapshot && snapshot.data && snapshot.data.tease);
        })
      );
  }

  ngOnDestroy() {
    if (this.tourSub) this.tourSub.unsubscribe();
  }

  toggleMenu(value?: boolean) {
    this.navOpen$.next(value !== undefined ? value : !this.navOpen$.getValue());
  }

  handleAuth(event?: Event | null) {
    if (!this.user$.getValue() && !this.minimal$.getValue()) {
      if (event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const elem = closest(event.target as HTMLElement, el => el.tagName.toLowerCase() === 'a');
        if (elem && elem.hasAttribute('href')) {
          const href = `/${elem.getAttribute('href').substring(this.basePath.length)}`;
          this.identificationCallback = () => this.Router.navigateByUrl(href);
        }
      }
      this.identifyModal.show();
    }
  }

  tourStepComplete(step: string) {
    if (step === 'rtLearnMore') {
      this.identificationCallback = () => this.Router.navigate(['/positions']);
      this.handleAuth();
    }
  }
}
